import {CSS} from '@dnd-kit/utilities';
import {Bars3Icon} from '@heroicons/react/24/solid';
import React, {FC, useState, useMemo, useCallback, useRef, useEffect} from 'react';
import {SortableContext, useSortable, rectSortingStrategy} from '@dnd-kit/sortable';
import {closestCenter, PointerSensor, useSensor, useSensors, useDroppable} from '@dnd-kit/core';
import {
  PencilSquareIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import {DndContext, DragEndEvent, DragOverEvent, DragOverlay, DragStartEvent} from '@dnd-kit/core';
import {createTemplateRole, updateTemplateRole, deleteTemplateRole, IRole, TRecipientType, VerdocsEndpoint} from '@verdocs/js-sdk';
import {RoleSettingsModal} from './RoleSettingsModal';
import {RoleToolbar} from './RoleToolbar';

interface TemplateRolesEditorProps {
  templateId: string;
  roles: IRole[];
  onRolesUpdated: () => void;
}

interface StepGroup {
  sequence: number;
  roles: IRole[];
}

const TYPE_ICONS: Record<TRecipientType, FC<{className?: string}>> = {
  signer: PencilSquareIcon,
  cc: EnvelopeIcon,
  approver: CheckBadgeIcon,
};

function getRoleColorVar(index: number): string {
  return `var(--signer-${(index % 10) + 1}-color)`;
}

function sortRoles(roles: IRole[]): IRole[] {
  return [...roles].sort((a, b) => (a.sequence === b.sequence ? a.order - b.order : a.sequence - b.sequence));
}

function groupRoles(roles: IRole[]): StepGroup[] {
  const sorted = sortRoles(roles);
  const groupMap = new Map<number, IRole[]>();
  sorted.forEach((role) => {
    if (!groupMap.has(role.sequence)) {
      groupMap.set(role.sequence, []);
    }
    groupMap.get(role.sequence)!.push(role);
  });
  return Array.from(groupMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([sequence, roles]) => ({sequence, roles}));
}

function getNextRoleName(roles: IRole[]): string {
  let n = roles.length;
  let name: string;
  do {
    n++;
    name = `Recipient ${n}`;
  } while (roles.some((r) => r.name === name));
  return name;
}

// ─── Droppable Group Container ─────────────────────────────────────────────

const DroppableGroup: FC<{id: string; children: React.ReactNode}> = ({id, children}) => {
  const {setNodeRef, isOver} = useDroppable({id});
  return (
    <div
      ref={setNodeRef}
      className={`grid grid-cols-1 tablet:grid-cols-3 gap-2 min-h-[48px] rounded-md p-2 transition-colors ${isOver ? 'bg-indigo-50' : ''}`}>
      {children}
    </div>
  );
};

// ─── Sortable Role Chip ────────────────────────────────────────────────────

interface RoleChipProps {
  role: IRole;
  colorIndex: number;
  isSelected: boolean;
  isConfirmingDelete: boolean;
  onSelect: (el: HTMLElement) => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
}

const RoleChip: FC<RoleChipProps> = ({role, colorIndex, isSelected, isConfirmingDelete, onSelect, onConfirmDelete, onCancelDelete}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: role.name,
    data: {type: 'role', role, sequence: role.sequence},
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    borderColor: getRoleColorVar(colorIndex),
    borderWidth: '2px',
    borderStyle: role.email ? 'solid' : 'dashed',
  };

  const TypeIcon = TYPE_ICONS[role.type];
  const isKnown = !!role.email;

  if (isConfirmingDelete) {
    return (
      <div
        ref={setNodeRef}
        style={{...style, borderColor: '#ef4444', borderStyle: 'solid'}}
        className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2.5 select-none">
        <span className="text-sm font-medium text-red-700">Delete?</span>
        <div className="flex items-center gap-2">
          <button
            onClick={onConfirmDelete}
            className="text-xs font-semibold text-red-700 hover:text-red-900 px-2 py-0.5 rounded hover:bg-red-100">
            Yes
          </button>
          <button
            onClick={onCancelDelete}
            className="text-xs font-semibold text-gray-600 hover:text-gray-800 px-2 py-0.5 rounded hover:bg-gray-100">
            No
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-role-name={role.name}
      className={`group flex items-center gap-2 rounded-lg bg-white px-3 py-2.5 cursor-pointer select-none transition-shadow hover:shadow-md ${
        isSelected ? 'ring-2 ring-indigo-400 ring-offset-1' : ''
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(e.currentTarget);
      }}>
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none">
        <Bars3Icon className="w-4 h-4" />
      </div>
      <TypeIcon className="w-4 h-4 flex-shrink-0 text-gray-500" />
      <span className="text-sm font-medium text-gray-800 truncate flex-1">{role.name}</span>
      {isKnown && (
        <span className="text-[10px] text-gray-400 truncate max-w-[80px] hidden tablet:inline" title={role.email!}>
          {role.email}
        </span>
      )}
    </div>
  );
};

// ─── Drag Overlay Chip (non-interactive copy shown while dragging) ──────

const DragOverlayChip: FC<{role: IRole; colorIndex: number}> = ({role, colorIndex}) => {
  const TypeIcon = TYPE_ICONS[role.type];
  return (
    <div
      className="flex items-center gap-2 rounded-lg bg-white px-3 py-2.5 shadow-xl border-2"
      style={{
        borderColor: getRoleColorVar(colorIndex),
        borderStyle: role.email ? 'solid' : 'dashed',
        width: 200,
      }}>
      <Bars3Icon className="w-4 h-4 text-gray-400" />
      <TypeIcon className="w-4 h-4 flex-shrink-0 text-gray-500" />
      <span className="text-sm font-medium text-gray-800 truncate">{role.name}</span>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────

export const TemplateRolesEditor: FC<TemplateRolesEditorProps> = ({templateId, roles: externalRoles, onRolesUpdated}) => {
  const endpoint = VerdocsEndpoint.getDefault();
  const [localRoles, setLocalRoles] = useState<IRole[]>(externalRoles || []);
  const [selectedRoleName, setSelectedRoleName] = useState<string | null>(null);
  const [selectedChipEl, setSelectedChipEl] = useState<HTMLElement | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [settingsRoleName, setSettingsRoleName] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const pendingSelectRef = useRef<string | null>(null);

  useEffect(() => {
    if (externalRoles) {
      setLocalRoles(externalRoles);
    }
  }, [externalRoles]);

  useEffect(() => {
    if (pendingSelectRef.current) {
      const name = pendingSelectRef.current;
      pendingSelectRef.current = null;
      const chipEl = document.querySelector(`[data-role-name="${name}"]`) as HTMLElement;
      if (chipEl) {
        setSelectedRoleName(name);
        setSelectedChipEl(chipEl);
        if (window.innerWidth < 768) {
          chipEl.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      }
    }
  });

  const sortedRoles = useMemo(() => sortRoles(localRoles), [localRoles]);
  const groups = useMemo(() => groupRoles(localRoles), [localRoles]);
  const selectedRole = useMemo(() => localRoles.find((r) => r.name === selectedRoleName) || null, [localRoles, selectedRoleName]);

  const roleColorMap = useMemo(() => {
    const map = new Map<string, number>();
    sortedRoles.forEach((role, i) => map.set(role.name, i));
    return map;
  }, [sortedRoles]);

  // ── DnD Setup ──

  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 8}}));

  const findGroupForRole = useCallback(
    (roleName: string) => {
      return groups.find((g) => g.roles.some((r) => r.name === roleName));
    },
    [groups],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
    setSelectedRoleName(null);
    setSelectedChipEl(null);
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const {active, over} = event;
      if (!over) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      const activeGroup = findGroupForRole(activeId);
      let overSequence: number | null = null;

      if (overId.startsWith('droppable-')) {
        overSequence = parseInt(overId.replace('droppable-', ''));
      } else {
        const overGroup = findGroupForRole(overId);
        if (overGroup) overSequence = overGroup.sequence;
      }

      if (activeGroup && overSequence !== null && activeGroup.sequence !== overSequence) {
        setLocalRoles((prev) => {
          const updated = prev.map((r) => {
            if (r.name === activeId) {
              const rolesInTarget = prev.filter((rr) => rr.sequence === overSequence && rr.name !== activeId);
              return {...r, sequence: overSequence!, order: rolesInTarget.length + 1};
            }
            return r;
          });
          return updated;
        });
      }
    },
    [findGroupForRole],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const {active} = event;
      setActiveDragId(null);

      const roleName = active.id as string;
      const role = localRoles.find((r) => r.name === roleName);
      if (!role) return;

      const rolesInGroup = localRoles.filter((r) => r.sequence === role.sequence).sort((a, b) => a.order - b.order);
      const newOrder = rolesInGroup.findIndex((r) => r.name === roleName) + 1;

      updateTemplateRole(endpoint, templateId, roleName, {
        sequence: role.sequence,
        order: newOrder,
      }).then(() => onRolesUpdated());
    },
    [localRoles, endpoint, templateId, onRolesUpdated],
  );

  // ── Role Actions ──

  const handleAddRole = useCallback(
    (sequence: number) => {
      const name = getNextRoleName(localRoles);
      const order = localRoles.filter((r) => r.sequence === sequence).length + 1;

      const newRole: IRole = {
        template_id: templateId,
        name,
        type: 'signer',
        full_name: null,
        first_name: null,
        last_name: null,
        email: null,
        phone: null,
        message: null,
        sequence,
        order,
        delegator: false,
        name_locked: false,
      };

      setLocalRoles((prev) => [...prev, newRole]);
      pendingSelectRef.current = name;

      createTemplateRole(endpoint, templateId, newRole).then(() => onRolesUpdated());
    },
    [localRoles, endpoint, templateId, onRolesUpdated],
  );

  const handleAddStep = useCallback(() => {
    const maxSequence = groups.length > 0 ? Math.max(...groups.map((g) => g.sequence)) : 0;
    handleAddRole(maxSequence + 1);
  }, [groups, handleAddRole]);

  const handleDeleteRole = useCallback(
    (roleName: string) => {
      setLocalRoles((prev) => prev.filter((r) => r.name !== roleName));
      setSelectedRoleName(null);
      setSelectedChipEl(null);
      setConfirmingDelete(null);

      deleteTemplateRole(endpoint, templateId, roleName).then(() => onRolesUpdated());
    },
    [endpoint, templateId, onRolesUpdated],
  );

  const handleTypeChange = useCallback(
    (type: TRecipientType) => {
      if (!selectedRoleName) return;
      setLocalRoles((prev) => prev.map((r) => (r.name === selectedRoleName ? {...r, type} : r)));
      updateTemplateRole(endpoint, templateId, selectedRoleName, {type}).then(() => onRolesUpdated());
    },
    [selectedRoleName, endpoint, templateId, onRolesUpdated],
  );

  const handleNameChange = useCallback(
    (newName: string) => {
      if (!selectedRoleName || newName === selectedRoleName) return;
      if (localRoles.some((r) => r.name === newName)) return;

      const oldName = selectedRoleName;
      setLocalRoles((prev) => prev.map((r) => (r.name === oldName ? {...r, name: newName} : r)));
      setSelectedRoleName(newName);
      updateTemplateRole(endpoint, templateId, oldName, {name: newName}).then(() => onRolesUpdated());
    },
    [selectedRoleName, localRoles, endpoint, templateId, onRolesUpdated],
  );

  const handleMoveRole = useCallback(
    (direction: 'up' | 'down') => {
      if (!selectedRoleName) return;

      const role = localRoles.find((r) => r.name === selectedRoleName);
      if (!role) return;

      const groupSequences = groups.map((g) => g.sequence);
      const currentGroupIdx = groupSequences.indexOf(role.sequence);
      const targetGroupIdx = direction === 'up' ? currentGroupIdx - 1 : currentGroupIdx + 1;

      if (targetGroupIdx < 0 || targetGroupIdx >= groupSequences.length) return;

      const targetSequence = groupSequences[targetGroupIdx];
      const rolesInTarget = localRoles.filter((r) => r.sequence === targetSequence);

      setLocalRoles((prev) =>
        prev.map((r) => (r.name === selectedRoleName ? {...r, sequence: targetSequence, order: rolesInTarget.length + 1} : r)),
      );

      updateTemplateRole(endpoint, templateId, selectedRoleName, {
        sequence: targetSequence,
        order: rolesInTarget.length + 1,
      }).then(() => onRolesUpdated());
    },
    [selectedRoleName, localRoles, groups, endpoint, templateId, onRolesUpdated],
  );

  const handleMoveGroup = useCallback(
    (sequence: number, direction: 'up' | 'down') => {
      const groupSequences = groups.map((g) => g.sequence);
      const idx = groupSequences.indexOf(sequence);
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= groupSequences.length) return;

      const swapSequence = groupSequences[swapIdx];

      const affectedRoles = localRoles.filter((r) => r.sequence === sequence || r.sequence === swapSequence);

      setLocalRoles((prev) =>
        prev.map((r) => {
          if (r.sequence === sequence) return {...r, sequence: swapSequence};
          if (r.sequence === swapSequence) return {...r, sequence};
          return r;
        }),
      );

      const updates = affectedRoles.map((r) => {
        const newSeq = r.sequence === sequence ? swapSequence : sequence;
        return updateTemplateRole(endpoint, templateId, r.name, {sequence: newSeq});
      });

      Promise.all(updates).then(() => onRolesUpdated());
    },
    [groups, localRoles, endpoint, templateId, onRolesUpdated],
  );

  const handleSettingsSave = useCallback(
    (updates: Partial<IRole>) => {
      if (!settingsRoleName) return;
      setLocalRoles((prev) => prev.map((r) => (r.name === settingsRoleName ? {...r, ...updates} : r)));
      updateTemplateRole(endpoint, templateId, settingsRoleName, updates).then(() => onRolesUpdated());
    },
    [settingsRoleName, endpoint, templateId, onRolesUpdated],
  );

  const handleSelectRole = useCallback(
    (roleName: string, el: HTMLElement) => {
      if (selectedRoleName === roleName) {
        setSelectedRoleName(null);
        setSelectedChipEl(null);
      } else {
        setSelectedRoleName(roleName);
        setSelectedChipEl(el);
        setConfirmingDelete(null);

        if (window.innerWidth < 768) {
          setTimeout(() => {
            el.scrollIntoView({behavior: 'smooth', block: 'center'});
          }, 100);
        }
      }
    },
    [selectedRoleName],
  );

  const handleBackdropClick = useCallback(() => {
    setSelectedRoleName(null);
    setSelectedChipEl(null);
    setConfirmingDelete(null);
  }, []);

  // ── Toolbar position helpers ──

  const canMoveUp = useMemo(() => {
    if (!selectedRole) return false;
    const groupSequences = groups.map((g) => g.sequence);
    return groupSequences.indexOf(selectedRole.sequence) > 0;
  }, [selectedRole, groups]);

  const canMoveDown = useMemo(() => {
    if (!selectedRole) return false;
    const groupSequences = groups.map((g) => g.sequence);
    return groupSequences.indexOf(selectedRole.sequence) < groupSequences.length - 1;
  }, [selectedRole, groups]);

  // ── Dragged role for overlay ──

  const draggedRole = useMemo(
    () => (activeDragId ? localRoles.find((r) => r.name === activeDragId) || null : null),
    [activeDragId, localRoles],
  );

  // ── Render ──

  if (localRoles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-gray-400 mb-3">
          <PlusIcon className="w-12 h-12 mx-auto" />
        </div>
        <p className="text-gray-600 mb-1 font-medium">No roles yet</p>
        <p className="text-gray-400 text-sm mb-6">Add recipients to define your signing workflow</p>
        <button
          onClick={handleAddStep}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors">
          <PlusIcon className="w-4 h-4" />
          Add your first role
        </button>
      </div>
    );
  }

  return (
    <div onClick={handleBackdropClick}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}>
        <div className="flex flex-col gap-0">
          {groups.map((group, groupIndex) => (
            <React.Fragment key={group.sequence}>
              {groupIndex > 0 && (
                <div className="flex items-center justify-center py-1">
                  <ArrowDownIcon className="w-5 h-5 text-gray-300" />
                </div>
              )}

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold">
                      {groupIndex + 1}
                    </span>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Step {groupIndex + 1}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveGroup(group.sequence, 'up');
                      }}
                      disabled={groupIndex === 0}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent"
                      title="Move step up">
                      <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveGroup(group.sequence, 'down');
                      }}
                      disabled={groupIndex === groups.length - 1}
                      className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent"
                      title="Move step down">
                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                </div>

                <SortableContext items={group.roles.map((r) => r.name)} strategy={rectSortingStrategy}>
                  <DroppableGroup id={`droppable-${group.sequence}`}>
                    {group.roles.map((role) => (
                      <RoleChip
                        key={role.name}
                        role={role}
                        colorIndex={roleColorMap.get(role.name) || 0}
                        isSelected={selectedRoleName === role.name}
                        isConfirmingDelete={confirmingDelete === role.name}
                        onSelect={(el) => handleSelectRole(role.name, el)}
                        onConfirmDelete={() => handleDeleteRole(role.name)}
                        onCancelDelete={() => setConfirmingDelete(null)}
                      />
                    ))}
                  </DroppableGroup>
                </SortableContext>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddRole(group.sequence);
                  }}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-50 transition-colors">
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add Role
                </button>
              </div>
            </React.Fragment>
          ))}
        </div>

        <DragOverlay>
          {draggedRole ? <DragOverlayChip role={draggedRole} colorIndex={roleColorMap.get(draggedRole.name) || 0} /> : null}
        </DragOverlay>
      </DndContext>

      <div className="flex justify-center mt-4">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleAddStep();
          }}
          className="inline-flex items-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">
          <PlusIcon className="w-4 h-4" />
          Add Step
        </button>
      </div>

      {selectedRole && selectedChipEl && !activeDragId && (
        <RoleToolbar
          role={selectedRole}
          anchorEl={selectedChipEl}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          onTypeChange={handleTypeChange}
          onNameChange={handleNameChange}
          onMoveUp={() => handleMoveRole('up')}
          onMoveDown={() => handleMoveRole('down')}
          onDelete={() => setConfirmingDelete(selectedRoleName)}
          onSettings={() => {
            setSettingsRoleName(selectedRoleName);
          }}
          onClose={() => {
            setSelectedRoleName(null);
            setSelectedChipEl(null);
          }}
        />
      )}

      <RoleSettingsModal
        role={localRoles.find((r) => r.name === settingsRoleName) || null}
        open={!!settingsRoleName}
        onClose={() => setSettingsRoleName(null)}
        onSave={handleSettingsSave}
      />
    </div>
  );
};
